import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useUserRole } from "./useUserRole";
import { useAuth } from "@/contexts/AuthContext";
import { calendarDatesFromPosition, calendarDatesFromSelection } from "@/lib/calendarDates";

function getLocalDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

interface GanttItem {
  id: string;
  title: string;
  notes: string | null;
  start_position: number;
  width: number;
  color?: string;
  google_event_id?: string | null;
  start_time?: string | null;
  is_all_day?: boolean;
  time_duration?: number;
  start_date?: string | null;
  end_date?: string | null;
}

interface WorkspaceInfo {
  workspaceType: 'personal' | 'group';
  groupId: string | null;
  workspaceName: string;
}

const BASE_DAY_WIDTH = 80;

export const useGoogleCalendarSync = (workspaceInfo?: WorkspaceInfo) => {
  const { session } = useAuth();
  const sessionRef = useRef(session);
  const { canAccessCalendarSync, isDemo, loading: roleLoading } = useUserRole();
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const syncQueueRef = useRef<
    Map<
      string,
      { item: GanttItem; leadName: string; leadId: string; startDate: string; endDate: string; workspace: WorkspaceInfo | undefined }
    >
  >(new Map());
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const getItemDateStrings = useCallback((item: GanttItem): { startDate: string; endDate: string } => {
    if (item.start_date && item.end_date) {
      return { startDate: item.start_date, endDate: item.end_date };
    }
    const dates = item.start_date
      ? calendarDatesFromSelection(item.start_date, item.width / BASE_DAY_WIDTH)
      : calendarDatesFromPosition(item.start_position, item.width);
    return { startDate: item.start_date ?? dates.start_date, endDate: dates.end_date };
  }, []);

  const checkConnection = useCallback(async () => {
    if (!canAccessCalendarSync) { setIsConnected(false); setIsLoading(false); return; }
    try {
      const currentSession = sessionRef.current;
      if (!currentSession) { setIsConnected(false); setIsLoading(false); return; }
      const response = await supabase.functions.invoke('google-calendar-sync/status', {
        headers: { Authorization: `Bearer ${currentSession.access_token}` },
      });
      setIsConnected(response.data?.connected ?? false);
    } catch (error) {
      console.error('Error checking calendar connection:', error);
      setIsConnected(false);
    } finally {
      setIsLoading(false);
    }
  }, [canAccessCalendarSync]);

  useEffect(() => {
    checkConnection();
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'GOOGLE_CALENDAR_CONNECTED') {
        setIsConnected(true);
        toast.success('Google Calendar connected!');
        window.dispatchEvent(new CustomEvent('google-calendar-connection-changed', { detail: { connected: true } }));
      }
    };
    const handleConnectionChange = (e: Event) => {
      const detail = (e as CustomEvent<{ connected: boolean }>).detail;
      if (typeof detail?.connected === 'boolean') setIsConnected(detail.connected);
      else checkConnection();
    };
    window.addEventListener('message', handleMessage);
    window.addEventListener('google-calendar-connection-changed', handleConnectionChange);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('google-calendar-connection-changed', handleConnectionChange);
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    };
  }, [checkConnection]);

  const connect = async () => {
    if (!canAccessCalendarSync) {
      toast.error('Upgrade to access Google Calendar sync', { description: 'This feature is available for paid users only' });
      return;
    }
    try {
      const currentSession = sessionRef.current;
      if (!currentSession) { toast.error('Please sign in first'); return; }
      const response = await supabase.functions.invoke('google-calendar-sync/auth', {
        headers: { Authorization: `Bearer ${currentSession.access_token}` },
      });
      if (response.error) throw new Error(response.error.message);
      if (response.data?.authUrl) {
        const popup = window.open(response.data.authUrl, 'google-calendar-auth', 'width=500,height=600,scrollbars=yes');
        if (!popup) toast.error('Please allow popups to connect Google Calendar');
      }
    } catch (error) {
      console.error('Error connecting to Google Calendar:', error);
      toast.error('Failed to connect to Google Calendar');
    }
  };

  const disconnect = async () => {
    try {
      const currentSession = sessionRef.current;
      if (!currentSession) return;
      const response = await supabase.functions.invoke('google-calendar-sync/disconnect', {
        method: 'POST',
        headers: { Authorization: `Bearer ${currentSession.access_token}` },
      });
      if (response.error) throw new Error(response.error.message);
      setIsConnected(false);
      window.dispatchEvent(new CustomEvent('google-calendar-connection-changed', { detail: { connected: false } }));
      toast.success('Google Calendar disconnected');
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Failed to disconnect');
    }
  };

  // Auto-sync with debounce + BATCH mode — queued items sent in a single request
  const syncItem = useCallback(async (item: GanttItem, leadName: string, leadId: string, _baseDate?: string, silent: boolean = false) => {
    if (!isConnected || !canAccessCalendarSync) return;

    const browserTimezone = getBrowserTimezone();
    const { startDate, endDate } = getItemDateStrings(item);
    syncQueueRef.current.set(item.id, { item, leadName, leadId, startDate, endDate, workspace: workspaceInfo });

    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);

    syncTimeoutRef.current = setTimeout(async () => {
      const queue = new Map(syncQueueRef.current);
      syncQueueRef.current.clear();
      if (queue.size === 0) return;

      try {
        const currentSession = sessionRef.current;
        if (!currentSession) return;

        // Batch all queued items into a single request
        const itemsArray = Array.from(queue.values()).map(({ item: queuedItem, leadName: name, leadId: lid, startDate: sd, endDate: ed, workspace }) => ({
          item: queuedItem,
          leadName: name,
          leadId: lid,
          startDate: sd,
          endDate: ed,
          workspaceType: workspace?.workspaceType,
          groupId: workspace?.groupId,
          workspaceName: workspace?.workspaceName,
        }));

        const response = await supabase.functions.invoke('google-calendar-sync/sync-item', {
          method: 'POST',
          headers: { Authorization: `Bearer ${currentSession.access_token}` },
          body: {
            items: itemsArray,
            timezone: browserTimezone,
          },
        });

        const syncedCount = response.data?.synced || 0;
        if (syncedCount > 0 && !silent) {
          toast.success(`Synced to Google Calendar`, { duration: 2000 });
        }
      } catch (error) {
        console.error('Error auto-syncing items:', error);
      }
    }, 1500);
  }, [isConnected, workspaceInfo, canAccessCalendarSync, getItemDateStrings]);

  const syncGroupItem = useCallback(async (
    item: GanttItem, leadName: string, leadId: string, action: 'upsert' | 'delete' = 'upsert',
  ) => {
    if (!canAccessCalendarSync || !workspaceInfo?.groupId) return;
    const browserTimezone = getBrowserTimezone();
    const { startDate, endDate } = getItemDateStrings(item);
    try {
      const currentSession = sessionRef.current;
      if (!currentSession) return;
      await supabase.functions.invoke('google-calendar-sync/sync-group', {
        method: 'POST',
        headers: { Authorization: `Bearer ${currentSession.access_token}` },
        body: { item, leadName, leadId, startDate, endDate, timezone: browserTimezone, groupId: workspaceInfo.groupId, workspaceName: workspaceInfo.workspaceName, action },
      });
    } catch (error) {
      console.error('Error syncing group item to all members:', error);
    }
  }, [canAccessCalendarSync, workspaceInfo, getItemDateStrings]);

  const deleteItem = useCallback(async (googleEventId: string | null | undefined, ganttItemId?: string) => {
    if (!isConnected || (!googleEventId && !ganttItemId) || !canAccessCalendarSync) return;
    try {
      const currentSession = sessionRef.current;
      if (!currentSession) return;
      await supabase.functions.invoke('google-calendar-sync/delete-item', {
        method: 'POST',
        headers: { Authorization: `Bearer ${currentSession.access_token}` },
        body: { googleEventId, ganttItemId, workspaceType: workspaceInfo?.workspaceType, groupId: workspaceInfo?.groupId, workspaceName: workspaceInfo?.workspaceName },
      });
    } catch (error) {
      console.error('Error deleting calendar event:', error);
    }
  }, [isConnected, workspaceInfo, canAccessCalendarSync]);

  const pullChanges = useCallback(async (leadId: string, baseDate?: string): Promise<number> => {
    if (!isConnected || !canAccessCalendarSync) return 0;
    try {
      const currentSession = sessionRef.current;
      if (!currentSession) return 0;
      const response = await supabase.functions.invoke('google-calendar-sync/pull-changes', {
        method: 'POST',
        headers: { Authorization: `Bearer ${currentSession.access_token}` },
        body: {
          leadId, baseDate: baseDate || getLocalDateString(), timezone: getBrowserTimezone(),
          workspaceType: workspaceInfo?.workspaceType, groupId: workspaceInfo?.groupId, workspaceName: workspaceInfo?.workspaceName,
        },
      });
      if (response.data?.needsReconnect) { setIsConnected(false); toast.error('Session expired. Please reconnect Google Calendar'); return 0; }
      return response.data?.updated || 0;
    } catch (error) {
      console.error('Error pulling calendar changes:', error);
      return 0;
    }
  }, [isConnected, workspaceInfo, canAccessCalendarSync]);

  // Bulk sync using batch endpoint — single request instead of N requests
  const syncItems = async (leadId: string, items: GanttItem[], leadName: string) => {
    if (!canAccessCalendarSync) {
      toast.error('Upgrade to access Google Calendar sync', { description: 'This feature is available for paid users only' });
      return false;
    }
    if (!isConnected) { toast.error('Please connect Google Calendar first'); return false; }
    if (items.length === 0) { toast.info('No tasks to sync'); return false; }

    setIsSyncing(true);
    try {
      const currentSession = sessionRef.current;
      if (!currentSession) { toast.error('Please sign in first'); return false; }

      const browserTimezone = getBrowserTimezone();
      const itemsArray = items.map(item => {
        const { startDate, endDate } = getItemDateStrings(item);
        return {
          item, leadName, leadId, startDate, endDate,
          workspaceType: workspaceInfo?.workspaceType, groupId: workspaceInfo?.groupId, workspaceName: workspaceInfo?.workspaceName,
        };
      });

      const response = await supabase.functions.invoke('google-calendar-sync/sync-item', {
        method: 'POST',
        headers: { Authorization: `Bearer ${currentSession.access_token}` },
        body: { items: itemsArray, timezone: browserTimezone },
      });

      const syncedCount = response.data?.synced || 0;
      if (syncedCount > 0) {
        toast.success(`${syncedCount} event(s) synced to Google Calendar`);
        return true;
      } else {
        toast.error('Failed to sync events');
        return false;
      }
    } catch (error: any) {
      console.error('Error syncing to Google Calendar:', error);
      if (error.message?.includes('Token expired') || error.message?.includes('reconnect')) {
        setIsConnected(false);
        toast.error('Session expired. Please reconnect Google Calendar');
      } else {
        toast.error('Failed to sync to Google Calendar');
      }
      return false;
    } finally {
      setIsSyncing(false);
    }
  };

  const syncAll = useCallback(async (leadId: string, leadName: string, items: GanttItem[]) => {
    if (!canAccessCalendarSync) {
      toast.error('Upgrade to access Google Calendar sync', { description: 'This feature is available for paid users only' });
      return { success: false, pushed: 0, pulled: 0 };
    }
    if (!isConnected) {
      toast.error('Please connect Google Calendar first');
      return { success: false, pushed: 0, pulled: 0 };
    }
    setIsSyncing(true);
    try {
      const pulledCount = await pullChanges(leadId);
      const pushedOk = await syncItems(leadId, items, leadName);
      const pushedCount = pushedOk ? items.length : 0;
      return { success: pushedOk || pulledCount > 0, pushed: pushedCount, pulled: pulledCount };
    } finally {
      setIsSyncing(false);
    }
  }, [isConnected, pullChanges, canAccessCalendarSync]);

  return {
    isConnected, isLoading, isSyncing, canAccessCalendarSync,
    connect, disconnect, syncAll, syncItem, syncGroupItem, deleteItem, pullChanges, syncItems, checkConnection,
  };
};
